import React, { createRef, RefObject, useEffect, useMemo, useRef } from 'react';
import { isEqual, isNil } from 'lodash';
import styled from 'styled-components';
import { getTimeUnitText, renderBracketRange } from '../../../../../../utils';
import { TimeUnit } from '../../../../../../utils/apollo/graphqlSchema';
import { PrimaryButton } from '../../../../../../components/Button';
import { MaybeNil } from '../../../../../../utils/interfaces';
import { useImmer } from 'use-immer';
import { NumberInputField } from '../../../../../../components/Form';
import { z, ZodIssueCode } from 'zod';
import { theme } from '../../../../../../styles/theme';

const NUM_BRACKETS = 10;

const Container = styled.div`
  min-width: 240px;
`;

const Bracket = styled.div`
  display: grid;
  grid-template-columns: 20px 75px 1fr;
  align-items: center;
  column-gap: 8px;
`;

const Text = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  color: ${theme.colors.secondary800};
`;

const Label = styled(Text)`
  text-align: right;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  padding: 12px 4px;
`;

const StyledButton = styled(PrimaryButton)`
  width: 100%;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
`;

const bracketsValidator = z
  .array(z.unknown())
  .transform((arg) => {
    // remove any unused brackets at the end of the array

    let lastNonNilIndex: number | null = null;
    for (let i = arg.length - 1; i >= 0; i--) {
      if (!isNil(arg[i])) {
        lastNonNilIndex = i;
        break;
      }
    }
    if (lastNonNilIndex !== null) {
      // end is exclusive, thus + 1
      return arg.slice(0, lastNonNilIndex + 1);
    } else {
      return [];
    }
  })
  .pipe(
    z
      .array(z.number().min(1).max(1000).nullable())
      .superRefine((arg, ctx): arg is number[] => {
        for (let i = 0; i < arg.length; i++) {
          const element = arg[i];
          if (isNil(element)) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              path: [i],
              message: 'Gaps are not allowed',
            });
          }
        }
        // The return value is not used, but we need to return something
        // to satisfy the type guard
        return z.NEVER;
      }),
  );

const bracketsToStrictNumberValue = z.number().catch(0).array();

interface IProps {
  type: TimeUnit;
  onApply: (value: { type: TimeUnit; data: number[] }) => void;
  values: number[];
}

export default ({ type, onApply, values }: IProps) => {
  // initialize a slot for each bracket value. Avoids pitfalls when the array
  // becomes sparse if user enters values in non-sequential order
  const [brackets, setBrackets] = useImmer<MaybeNil<number>[]>(
    Array.from({ length: NUM_BRACKETS }, () => null),
  );

  const parsedBrackets = useMemo(
    () => bracketsValidator.safeParse(brackets),
    [brackets],
  );

  const inputRefsArray = useRef(
    Array.from({ length: NUM_BRACKETS }, () => createRef<HTMLInputElement>()),
  );

  useEffect(() => {
    setBrackets((draft) => {
      for (let i = 0; i < draft.length; i++) {
        if (!isNil(values) && i < values.length) {
          draft[i] = values[i];
        } else {
          draft[i] = null;
        }
      }
    });
  }, [setBrackets, values]);

  const onTimeBracketChange = (newValue: number | null, index: number) => {
    setBrackets((draft) => {
      draft[index] = newValue;
    });
  };

  const onClickApply = () => {
    if (parsedBrackets.success) {
      const payload = {
        type,
        data: parsedBrackets.data,
      };
      onApply(payload);
    }
  };

  const getError = (index: number) =>
    !parsedBrackets.success
      ? parsedBrackets.error.errors.find((e) => e.path[0] === index)
      : undefined;

  const onKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    i: number,
  ) => {
    // when the user presses Tab, move to the next input
    // if combined with Shift - move to the previous input
    if (event.key === 'Tab') {
      event.preventDefault();
      let nextIndex = i + (event.shiftKey ? -1 : 1);
      const firstIndex = 0;
      const lastIndex = inputRefsArray.current.length - 1;
      if (nextIndex < firstIndex) {
        nextIndex = lastIndex;
      } else if (nextIndex > lastIndex) {
        nextIndex = firstIndex;
      }
      const nextInput = inputRefsArray.current[nextIndex].current;
      nextInput?.focus();
      nextInput?.select();
    }
  };

  const bracketRange = bracketsToStrictNumberValue.parse(brackets);

  const renderBracket = (ref: RefObject<HTMLInputElement>, index: number) => {
    const errorProps = getError(index);
    return (
      <Bracket key={'Bracket-' + index}>
        <Label>{index + 1}.</Label>
        <NumberInputField
          ref={ref}
          key={'Input-' + index}
          placeholder="e.g. 5"
          onChange={(value) => onTimeBracketChange(value, index)}
          value={brackets[index] ?? null}
          onKeyDown={(ev) => onKeyDown(ev, index)}
          onClick={(ev) => ev.stopPropagation()}
          onBlur={(ev) => ev.stopPropagation()}
          precision={0}
          errorMessage={errorProps?.message}
          hasError={!!errorProps}
        />
        <Text>
          {getTimeUnitText(type).singular}{' '}
          {renderBracketRange(index, bracketRange)}
        </Text>
      </Bracket>
    );
  };

  const isApplyDisabled =
    !parsedBrackets.success ||
    parsedBrackets.data.length === 0 ||
    isEqual(values, parsedBrackets.data);

  return (
    <Container>
      <Content>
        {inputRefsArray.current.map((ref, i) => renderBracket(ref, i))}
      </Content>
      <StyledButton
        type="button"
        onClick={onClickApply}
        disabled={isApplyDisabled}
        trackContext="time brackets"
        trackAction="apply"
      >
        Apply
      </StyledButton>
    </Container>
  );
};
